import React from 'react';
import { Tooltip } from './Tooltip';
import { Button } from './Button';

export type HeaderValues = {
  invoiceNumber: string;
  issueDate: string;
};

type Props = HeaderValues & {
  onChange: (values: Partial<HeaderValues>) => void;
  onReset: () => void;
  onPrint: () => void;
  onSavePdf: () => void;
  itemCount?: number;
  loadingPrint?: boolean;
  loadingSavePdf?: boolean;
  lastExportFileName?: string | null;
  confirmResetVisible?: boolean;
  onConfirmReset?: () => void;
  onCancelReset?: () => void;
  canExport?: boolean;
  validationErrors?: string[];
  className?: string;
};

export function InvoiceHeader(props: Props) {
  const { invoiceNumber, issueDate, onChange, onReset, onPrint, onSavePdf, itemCount = 0, loadingPrint = false, loadingSavePdf = false, lastExportFileName, confirmResetVisible = false, onConfirmReset, onCancelReset, canExport, validationErrors = [], className = '' } = props;

  // Fallback auf alte Logik falls canExport nicht gesetzt ist
  const effectiveCanExport = canExport ?? (itemCount > 0);
  const firstError = validationErrors[0];
  const disabledReason = !effectiveCanExport ? (firstError || 'Pflichtfelder ausfüllen') : null;

  function buildPrintTooltip(): string {
    if (loadingPrint) return 'Drucken läuft…';
    if (disabledReason) return `Nicht bereit: ${disabledReason}`;
    return 'Rechnung drucken (PDF aus Vorschau)';
  }
  function buildSaveTooltip(): string {
    if (loadingSavePdf) return 'PDF wird gespeichert…';
    if (disabledReason) return `Nicht bereit: ${disabledReason}`;
    return lastExportFileName ? `Letzte Datei: ${lastExportFileName}` : 'PDF speichern (herunterladen)';
  }

  const printTooltip = buildPrintTooltip();
  const saveTooltip = buildSaveTooltip();

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`.trim()} data-testid="invoice-header">
      <h2 className="font-semibold mb-3">Rechnungsdaten</h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 flex gap-3">
          <div className="flex-1">
            <label htmlFor="invoiceNumberInput" className="block text-xs font-medium text-slate-600 mb-1">Rechnungsnummer</label>
            <input id="invoiceNumberInput" className="w-full border border-slate-300 rounded px-3 py-2 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={invoiceNumber} onChange={(e) => onChange({ invoiceNumber: e.target.value })} />
          </div>
          <div className="w-44">
            <label htmlFor="issueDateInput" className="block text-xs font-medium text-slate-600 mb-1">Datum</label>
            <input id="issueDateInput" type="date" className="w-full border border-slate-300 rounded px-3 py-2 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={issueDate} onChange={(e) => onChange({ issueDate: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="mt-4 flex gap-2 items-center flex-wrap" data-testid="invoice-header-actions">
        {confirmResetVisible ? (
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-300 rounded px-2 py-1" data-testid="reset-confirm-inline">
            <span className="text-xs text-slate-700">Aktuelle Eingaben verwerfen?</span>
            <Button variant="danger" onClick={onConfirmReset} data-testid="reset-confirm-yes-btn">Ja</Button>
            <Button variant="secondary" onClick={onCancelReset} data-testid="reset-confirm-no-btn">Nein</Button>
          </div>
        ) : (
          <Tooltip content="Neue Rechnung starten (aktuelle Eingaben werden verworfen)">
            <Button variant="primary" onClick={onReset} data-testid="new-invoice-btn">Neue Rechnung</Button>
          </Tooltip>
        )}
        <Tooltip content={printTooltip}>
          <Button variant="primary" onClick={onPrint} disabled={!!disabledReason || loadingPrint} loading={loadingPrint} data-testid="print-invoice-btn">Drucken</Button>
        </Tooltip>
        <Tooltip content={saveTooltip}>
          <Button variant="secondary" onClick={onSavePdf} disabled={!!disabledReason || loadingSavePdf} loading={loadingSavePdf} data-testid="save-pdf-btn">PDF speichern</Button>
        </Tooltip>
      </div>
    </div>
  );
}
